import React from 'react';
import { LEVELS, getCurrentLevel, getNextLevel } from '../config/levels';
import './TrainingProgress.css';

const TrainingProgress = ({ 
  progress,
  isVisible,
  isChatLoading,
  isCreatingProfile,
  onStartChat
}) => {
  const currentLevel = getCurrentLevel(progress);
  const nextLevel = getNextLevel(currentLevel);
  
  // Show notifications based on profile creation state
  React.useEffect(() => {
    // Check if notification already exists to prevent duplicates
    const existingNotification = document.getElementById('profile-creation-notification');
    
    if (isCreatingProfile) {
      // Only create notification if it doesn't already exist
      if (!existingNotification) {
        // Show loading notification
        const notification = document.createElement('div');
        notification.className = 'profile-creation-notification';
        notification.id = 'profile-creation-notification';
        notification.innerHTML = `
          <div class="notification-content">
            <h3>Creating Level ${currentLevel.level} Profile: ${currentLevel.title}</h3>
            <p>Your mind is evolving to the next level. You can continue your interview while we create your character profile...</p>
            <div class="notification-loading">
              <div class="loading-spinner"></div>
            </div>
          </div>
        `;
        document.body.appendChild(notification);
      }
    } else {
      // When profile creation finishes, show success notification if user has reached or exceeded the threshold
      if (existingNotification) {
        if (progress >= currentLevel.answersRequired) {
          existingNotification.innerHTML = `
            <div class="notification-content">
              <h3>Level ${currentLevel.level} Achieved: ${currentLevel.title}!</h3>
              <p>Congratulations! Your mind clone has evolved to level ${currentLevel.level} and created a new character profile.</p>
              <p>Would you like to continue training or start a conversation with your evolved mind?</p>
              <div class="notification-actions">
                <button class="keep-training-btn" onclick="this.parentElement.parentElement.parentElement.remove()">
                  Keep Training
                </button>
                <button class="start-chat-btn" onclick="(() => { 
                  this.parentElement.parentElement.parentElement.remove(); 
                  document.dispatchEvent(new CustomEvent('startChat'));
                })()">
                  Start Chat
                </button>
              </div>
            </div>
          `;
        } else {
          // Only remove the notification if the user hasn't reached the threshold yet
          // This shouldn't typically happen, but is a safeguard
          existingNotification.remove();
        }
      }
    }
    
    // Clean up notification when component unmounts
    return () => {
      // We don't need any cleanup here since notifications are handled by:
      // 1. User actions (clicking buttons)
      // 2. Explicit removal when switching minds or toggling conversation mode in Main.js
      // Component re-renders don't affect notifications since they're attached to the document body
    };
  }, [isCreatingProfile, currentLevel, progress]);
  
  // Calculate percentage to next level
  const calculateLevelProgress = () => {
    if (!nextLevel) return 100; // Max level reached
    
    const answersInCurrentLevel = nextLevel.answersRequired - currentLevel.answersRequired;
    const progressInCurrentLevel = progress - currentLevel.answersRequired;
    
    return Math.min((progressInCurrentLevel / answersInCurrentLevel) * 100, 100);
  };

  const percentage = calculateLevelProgress();
  
  // Listen for the startChat event
  React.useEffect(() => {
    const handleStartChat = () => {
      onStartChat();
    };
    
    document.addEventListener('startChat', handleStartChat);
    return () => {
      document.removeEventListener('startChat', handleStartChat);
    };
  }, [onStartChat]);
  
  if (!isVisible) return null;
  
  return (
    <div className={`training-progress ${!isChatLoading ? 'visible' : ''}`}>
      <div className="training-progress-container">
        <div className="training-progress-row">
          <div className="progression-visualization">
            {LEVELS.map((level, index) => {
              // Calculate progress for this specific level segment
              const isCurrentLevel = currentLevel.level === level.level;
              const nextLevelThreshold = LEVELS[index + 1]?.answersRequired || LEVELS[LEVELS.length - 1].answersRequired;
              const levelStart = level.answersRequired;
              const levelRange = nextLevelThreshold - levelStart;
              const progressInLevel = Math.max(0, Math.min(progress - levelStart, levelRange));
              const levelPercentage = (progressInLevel / levelRange) * 100;

              // Determine what number to show (if any)
              let displayNumber = null;
              if (isCurrentLevel) {
                // Show current progress count for active level
                displayNumber = progress;
              } else if (progress < level.answersRequired) {
                // Show milestone number for future levels
                displayNumber = level.answersRequired;
              }
              // For completed levels, displayNumber remains null

              return (
                <div 
                  key={level.level} 
                  className={`level-node ${progress >= level.answersRequired ? 'achieved' : ''} ${isCurrentLevel ? 'current' : ''}`}
                >
                  <div className="node-content">
                    <div className="node-milestone">
                      <span className={`milestone-number ${displayNumber === null ? 'invisible' : ''}`}>
                        {displayNumber || level.answersRequired}
                      </span>
                      <div className={`level-progress-bar ${progress >= level.answersRequired ? 'achieved' : ''}`}>
                        <div 
                          className="level-progress-fill"
                          style={{ 
                            width: `${progress >= nextLevelThreshold ? 100 : levelPercentage}%`,
                            opacity: progress < level.answersRequired ? 0.3 : 1
                          }}
                        />
                      </div>
                    </div>
                    <span className="node-title">{level.title}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingProgress; 